import React, { useEffect, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import FormLabel from './FormLabel';
import Select from 'react-select';
import { IMaskInput } from 'react-imask';
import { alertModal } from '../utils/modalController';
import styles from './AddressForm.module.scss';

interface AddressFormProps {
    id: string;
    required?: boolean;
    className?: string;
    onAddressChange?: (id: string, address: any) => void;
    defaultCep?: string;
    defaultNumero?: string;
    defaultComplemento?: string;
    disabled?: boolean;
}

const apiUrl = process.env.REACT_APP_API_URL || '';

const AddressForm: React.FC<AddressFormProps> = ({ id, required, defaultCep, defaultComplemento, defaultNumero, className, disabled, onAddressChange }) => {
    const [cep, setCep] = useState('');
    const [logradouro, setLogradouro] = useState('');
    const [bairro, setBairro] = useState('');
    const [cidade, setCidade] = useState('');
    const [estado, setEstado] = useState('');
    const [numero, setNumero] = useState('');
    const [complemento, setComplemento] = useState('');
    const [isValidCep, setIsValidCep] = useState(true); // Verificar se o CEP é válido
    const [estados, setEstados] = useState<State[]>([]); // Lista de estados para o select

    useEffect(() => {
        const fetchStates = async () => {
            const response = await fetch(apiUrl + '/contas/ufs');

            if(!response.ok) {
                const data = await response.json();
                console.error('Erro ao carregar estados: ' + (data.message || 'Erro desconhecido'));
                return;
            }

            const data = await response.json();
            setEstados(data);
        };
        fetchStates();
    }, []);

    useEffect(() => {
        if (defaultCep) {
            // Aplica formatação
            const formattedCep = defaultCep.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2');
            setCep(formattedCep);

            if (formattedCep.length === 9) {
                fetchAddress(formattedCep);
            }
        }
        if (defaultNumero) {
            setNumero(defaultNumero);
        }
        if (defaultComplemento) {
            setComplemento(defaultComplemento);
        }
    }, [defaultCep]);


    // Função para buscar o endereço pelo CEP com fetch
    const fetchAddress = async (cep: string) => {
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
                setIsValidCep(false);
                setLogradouro('');
                setBairro('');
                setCidade('');
                setEstado('');
                return;
            }

            setIsValidCep(true);
            setLogradouro(data.logradouro || '');
            setBairro(data.bairro || '');
            setCidade(data.localidade || '');
            setEstado(data.uf || '');
        } catch (error) {
            setIsValidCep(false);
            console.error('Erro ao buscar o CEP:', error);
            alertModal('Erro ao buscar o CEP. Por favor, verifique o número.');
            setLogradouro('');
            setBairro('');
            setCidade('');
            setEstado('');
        }
    };

    // Função chamada no campo de CEP para buscar o endereço
    const handleCepChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputCep = event.target.value;
        setCep(inputCep);
        
        // Verifica se o CEP tem 8 caracteres (tamanho padrão de um CEP no Brasil)
        if (inputCep.replace(/\D/g, '').length === 8) {
            fetchAddress(inputCep);
        } else {
            setIsValidCep(true); // Reseta a validade quando o CEP não tem 8 caracteres
            setLogradouro('');
            setBairro('');
            setCidade('');
            setEstado('');
        }
    };

    useEffect(() => {
        if (onAddressChange) {
            onAddressChange(id, {
                cep,
                logradouro,
                bairro,
                cidade,
                estado,
                numero,
                complemento
            });
        }
    }, [cep, logradouro, bairro, cidade, estado, numero, complemento, id]);

    return (
        <div id={id} className={`address-form ${className || ''}`}>
            <Form.Group className="mb-3">
                <Row>
                    <Col md={6}>
                        <FormLabel htmlFor={`cep-${id}`} required={required}>
                            CEP:
                        </FormLabel>
                        <IMaskInput
                            mask="00000-000"
                            value={cep}
                            onAccept={(value: any) => handleCepChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>)}
                            className={`form-control ${!isValidCep ? 'is-invalid' : ''} ${styles['input-control']}`}
                            id={`cep-${id}`}    
                            name={`cep-${id}`}
                            required={required}
                            disabled={disabled}
                        />
                        {!isValidCep && (
                            <div className="invalid-feedback">
                                CEP inválido. Por favor, verifique o número.
                            </div>
                        )}

                    </Col>
                    <Col md={3}>
                        <FormLabel htmlFor={`number-${id}`} required={required}>
                            Número:
                        </FormLabel>
                        <Form.Control
                            type="text"
                            id={`number-${id}`}
                            name={`number-${id}`}
                            className={styles['input-control']}
                            defaultValue={numero}
                            onBlur={(e) => setNumero(e.target.value)}
                            required={required}
                            disabled={disabled}
                        />
                    </Col>
                    <Col md={3}>
                        <FormLabel htmlFor={`complement-${id}`}>
                            Complemento:
                        </FormLabel>
                        <Form.Control
                            type="text"
                            id={`complement-${id}`}
                            name={`complement-${id}`}
                            className={styles['input-control']}
                            defaultValue={complemento}
                            onBlur={(e) => setComplemento(e.target.value)}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            </Form.Group>

            <Form.Group className="mb-3">
                <Row>
                    <Col md={6}>
                        <FormLabel htmlFor={`logradouro-${id}`} required={required}>
                            Logradouro:
                        </FormLabel>
                        <Form.Control
                            type="text"
                            id={`logradouro-${id}`}
                            name={`logradouro-${id}`}
                            className={styles['input-control']}
                            value={logradouro}
                            onChange={(e) => setLogradouro(e.target.value)}
                            required={required}
                            disabled={disabled}
                        />

                    </Col>
                    <Col md={6}>

                        <FormLabel htmlFor={`bairro-${id}`} required={required}>
                            Bairro:
                        </FormLabel>
                        <Form.Control
                            type="text"
                            id={`bairro-${id}`}
                            name={`bairro-${id}`}
                            className={styles['input-control']}
                            value={bairro}
                            onChange={(e) => setBairro(e.target.value)}
                            required={required}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            </Form.Group>

            <Form.Group className="mb-3">
                <Row>
                    <Col md={6}>
                        <FormLabel htmlFor={`cidade-${id}`} required={required}>
                            Cidade:
                        </FormLabel>
                        <Form.Control
                            type="text"
                            id={`cidade-${id}`}
                            name={`cidade-${id}`}
                            className={styles['input-control']}
                            value={cidade}
                            onChange={(e) => setCidade(e.target.value)}
                            required={required}
                            disabled={disabled}
                        />
                    </Col>
                    <Col md={6}>
                        <FormLabel htmlFor={`estado-${id}`} required={required}>
                            Estado:
                        </FormLabel>
                        <Select
                            name={`estado-${id}`}
                            id={`estado-${id}`}
                            options={estados?.map(e => ({ label: e.name, value: e.code }))}
                            onChange={(values) => setEstado(values?.value || '')}
                            value={(estados ?? [])
                                .map(e => ({ label: e.name, value: e.code }))
                                .find(option => option.value === estado)}
                            required={required}
                            isDisabled={disabled}
                            classNames={{
                                control: () => styles['select-control'],
                                menu: () => styles['select-menu'],
                                option: () => styles['select-option'],
                                container: () => styles['select-container'],
                            }}
                        />
                    </Col>
                </Row>
            </Form.Group>
        </div>
    );
};

export default AddressForm;
